import { computed, defineComponent, h, ref, watch } from 'vue'

import { scroll } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { pushUrlState } from '../../../composables/useUrlFilters'
import { BsBtn, BsInput } from '../index'
import { replaceUrlParam } from 'booksprout'
import { domHandlers } from '../quasarHandlers'

// Quasar 2 split `setScrollPosition` into per-axis helpers; this only ever scrolled vertically.
const { getScrollTarget, setVerticalScrollPosition } = scroll

/**
 * Page navigation for the list screens: first / previous, a page number you can type into, next / last.
 *
 * It owns `currentPage` rather than reading it from the caller every render, which is why the prop that
 * seeds it is watched — a list that resets its own paging pushes the new page down through
 * `setCurrentPage`. `reset()` sets it back without emitting, so a re-filtered list does not load twice.
 *
 * It only shows when there is another page to go to [sc-28511]. Hidden rather than left out, so the list's
 * ref to it, and the reset through that ref, keep working.
 */
export default defineComponent({
  name: 'BsPaginator',

  /**
   * Declared so `input` is an emitted event rather than a fallthrough attribute: undeclared, the
   * `onInput` the render attaches stays in `$attrs` and Vue applies it to the root element too, so a
   * single DOM input event ran the handler twice.
   */
  emits: ['input'],

  props: {
    ...bsComponentProps,
    setCurrentPage: { type: Number, default: void 0 },
    totalRows: { type: Number, default: void 0 },
    rowsPerPage: { type: Number, default: void 0 },
    showMaxPages: { type: Number, default: 8 },
    updateUrl: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const vm = useVm()
    // `reset` is left out of the spread: this component's own reset puts the paging back to page one
    // rather than restoring a field's value, exactly as the class overrode the mixin's.
    const { reset, ...bs } = useBsComponent({ vm, props, emit })
    void reset

    const currentPage = ref(props.setCurrentPage || 1)

    const totalPages = computed(() => Math.ceil(props.totalRows / props.rowsPerPage))

    // One page, no rows, or a total that has not arrived yet (NaN pages).
    const hasNoOtherPages = computed(() => !(totalPages.value > 1))

    watch(() => props.setCurrentPage, (to: number) => {
      currentPage.value = to
    })

    return {
      ...bs,
      currentPage,
      totalPages,
      hasNoOtherPages
    }
  },

  methods: {
    setPage (pageNumber: number) {
      this.currentPage = pageNumber

      // Through the router's history, or the next click anywhere puts the url back (sc-28993).
      if (this.updateUrl) {
        const location = this.$router.options.history.location
        pushUrlState(this.$router, replaceUrlParam(location, 'page', pageNumber.toString()))
      }

      this.$emit('changed', {
        pageNumber,
        skip: pageNumber > 1 ? pageNumber * this.rowsPerPage - this.rowsPerPage : 0,
        take: this.rowsPerPage
      })
    },

    reset () {
      // Not calling set page here as we don't want to emit a change event
      this.currentPage = 1
    },

    /** Renamed from `_scrollToTop`: Vue does not proxy setup/method keys beginning with `_`. */
    scrollToTop () {
      const target = getScrollTarget(document.body)
      const offset = document.body.offsetTop
      const duration = 0
      setVerticalScrollPosition(target, offset, duration)
    },

    renderBtn (back: boolean, first = false, last = false) {
      const isDisabled = (back && this.currentPage === 1) || (!back && this.currentPage === this.totalPages)
      return h(BsBtn, {
        class: ['bs-paginator__nav-btn q-pa-none flex items-center font-size-8 justify-center', {
          'bs-paginator__nav-btn--disabled': isDisabled
        }],
        icon: back ? first ? 'app:double-arrow' : 'app:back' : last ? 'app:double-arrow' : 'app:forward',
        disable: isDisabled,
        secondary: true,
        ['data-cy']: back ? first ? 'paginatorFirstPageBtn' : 'paginatorPreviousPageBtn' : last ? 'paginatorLastPageBtn' : 'paginatorNextPageBtn',
        ['aria-label']: back ? first ? this.$t('system.labels.firstPage') : this.$t('system.labels.previousPage') : last ? this.$t('system.labels.lastPage') : this.$t('system.labels.nextPage'),
        onClick: () => {
            this.setPage(back ? first ? 1 : this.currentPage - 1 : last ? this.totalPages : this.currentPage + 1)
            this.scrollToTop()
          }
      // BsBtn is a component, so its children must be a slot function, not an array.
      }, { default: () => this.slot(this, 'default') })
    },

    renderPageButtons () {
      const result = []

      // Create the current page button
      result.push(
        h(BsInput, {
          class: ['bs-paginator__page-btn flex no-wrap flex-center', {
            'text-bold': true
          }],
          style: {
            'inline-size': this.totalPages < 10 ? '30px' : this.totalPages < 100 ? '40px' : '50px'
          },
          onInput: (value: string) => {
              setTimeout(() => {
                const pageNumber = parseInt(value)
                if (pageNumber >= 1 && pageNumber <= this.totalPages) {
                  this.setPage(pageNumber)
                }
              }, 1000)
            },
          value: this.currentPage.toString(),
          inputMode: 'numeric',
          // Nothing validates or auto-saves here, and the message row BsInput now always asks for made
          // the field 48px tall inside its 30px box, lifting the page number to the top edge.
          hideBottomSpace: true,
          pattern: '[0-9]*',
          min: 1,
          max: this.totalPages
        })
      )

      // Add "of TOTAL" text
      result.push(
        h('span', ` of ${this.totalPages}`)
      )

      return result
    },

    renderPageBtnContainer () {
      return h('div', {
        class: 'bs-paginator__page-btn-container flex'
      }, this.renderPageButtons())
    }
  },

  render () {
    return h('div', {
      class: ['bs-paginator flex items-center', this.getClasses({ hidden: this.hasNoOtherPages })],
      ...domHandlers(this.getListeners())
    }, [
      this.renderBtn(true, true),
      this.renderBtn(true),
      this.renderPageBtnContainer(),
      this.renderBtn(false),
      this.renderBtn(false, false, true)
    ])
  }
})
